import { Box, Flex, Grid, Heading, Spinner, VStack } from '@chakra-ui/react';
import { ConcertCard } from 'components/ConcertCard/ConcertCard';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useUpcomingConcertsQuery } from './hooks';
import { useEventTracking } from 'hooks/useEventTracking';

interface ConcertData {
  eventKey: string;
  eventId: number;
  title?: string;
  date?: string | null;
  stadium?: string | null;
}

export const Upcoming = () => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();
  const { isLoading, data } = useUpcomingConcertsQuery();

  const handleCardClick = (concert: ConcertData) => {
    eventTracking.event.clicked({
      eventId: concert.eventId,
      eventName: concert.title || concert.eventKey,
      eventDate: concert.date ?? null,
      venue: concert.stadium ?? undefined,
      source: 'concerts_page',
    });
    navigate('/concert/' + concert.eventKey);
  };

  return (
    <Box
      as='section'
      bg='BG100'
      px={{ base: 4, md: 6 }}
      py={{ base: 6, md: 8 }}
      color='dark.D900'
      textAlign={{ base: 'center', md: 'right' }}
      aria-labelledby='headingUpcoming'
      width='100vw'
    >
      <VStack width='100%' justifyContent='center' alignItems='center' spacing={6}>
        <Heading
          textAlign='center'
          id='headingUpcoming'
          mb={{ base: 6, md: 8 }}
          variant={{ base: 'headingMedium', md: 'headingLarge' }}
        >
          <FormattedMessage defaultMessage='Concerts' id='homepage.pages.concerts' />
        </Heading>
        {isLoading ? (
          <Flex align='center' justify='center' py={12}>
            <Spinner color='green.G50' size='xl' thickness='4px' />
          </Flex>
        ) : data?.items.length ? (
          <Box width='100%' display='flex' justifyContent='center' alignItems='center'>
            <Grid
              templateColumns={{
                base: 'repeat(1, minmax(0, 1fr))',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              }}
              gap={{ base: 4, md: 6 }}
              justifyContent='center'
              as='ul'
              listStyleType='none'
              width='100%'
              maxW='1200px'
              dir='rtl'
            >
              {data.items.map((concert) => (
                <Box as='li' key={concert.id} minW='0' width='100%' dir='ltr'>
                  <ConcertCard {...concert} onCardClick={() => handleCardClick(concert)} />
                </Box>
              ))}
            </Grid>
          </Box>
        ) : (
          <Flex align='center' justify='center' py={12}>
            <FormattedMessage
              defaultMessage='No upcoming Events'
              id='concerts.upcoming.noconcerts'
            />
          </Flex>
        )}
      </VStack>
    </Box>
  );
};
